import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../api/authFetch';

// MailVotes charges on doornoc's live Stripe account, same as the rest of
// the platform. The MailVotes-specific (test) key was retired; we still
// read it first for any local override, but production resolves to the
// generic live publishable key.
const STRIPE_PK =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_MAILVOTES ||
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
// Guard: in local dev the publishable key may be unset — loadStripe(undefined)
// throws at module load and crashes every page. Elements accepts stripe={null}.
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

// Server errors come as { error: 'text' } or { error: true, message: 'text' }.
// Rendering `error: true` showed an empty red box.
export function messageFrom(data, fallback) {
  if (data && typeof data.error === 'string' && data.error) return data.error;
  if (data && typeof data.message === 'string' && data.message) return data.message;
  return fallback;
}

const StripePaymentForm = ({
  amount,
  productType,
  tierLabel,
  onSuccess,
  onCancel,
  searchParameters,
  isCountLoading,
  totalCount,
  quoteError,
  intent
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  // What the server actually charges: it re-counts the search and prices it,
  // so this can differ from the preview's (sometimes estimated) count.
  const [serverQuote, setServerQuote] = useState(null);
  // Set once the card is charged. If recording the purchase then fails, the
  // button retries the confirmation instead of charging again.
  const [chargedIntentId, setChargedIntentId] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function createPaymentIntent() {
      if (!currentUser || !totalCount || totalCount < 1 || !productType) return;
      try {
        const response = await authFetch('/api/mailvotes/payments/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            count: totalCount,
            productType,
            searchParameters
          })
        });
        const data = await response.json();
        if (cancelled) return;
        if (data.success) {
          setClientSecret(data.clientSecret);
          setPaymentIntentId(data.paymentIntentId);
          setServerQuote({
            amountCents: data.amountCents,
            tierLabel: data.tierLabel,
            voterCount: data.voterCount
          });
        } else {
          setError(messageFrom(data, 'Failed to initialize payment'));
        }
      } catch (err) {
        if (!cancelled) setError('Failed to initialize payment');
        console.error('Payment intent creation error:', err);
      }
    }

    if (!isCountLoading) createPaymentIntent();
    return () => { cancelled = true; };
  }, [currentUser, totalCount, productType, searchParameters, isCountLoading]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    if (!clientSecret && !chargedIntentId) return;

    setLoading(true);
    setError('');

    let intentId = chargedIntentId;
    if (!intentId) {
      const cardElement = elements.getElement(CardElement);

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: currentUser.displayName || currentUser.email,
            email: currentUser.email
          }
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }
      if (paymentIntent.status !== 'succeeded') {
        setError(`Payment was not completed (${paymentIntent.status}).`);
        setLoading(false);
        return;
      }
      intentId = paymentIntent.id;
      setChargedIntentId(intentId);
    }

    try {
      // Re-send searchParameters in the body: Stripe metadata cannot hold a
      // long search (e.g. several voting-history filters). The server only
      // accepts it if it matches the search that was priced.
      const response = await authFetch('/api/mailvotes/payments/confirm-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentIntentId: intentId,
          searchParameters: searchParameters || {}
        })
      });
      const data = await response.json();
      if (data.success) {
        onSuccess(data.purchase);
      } else {
        setError(messageFrom(data, 'Payment confirmation failed'));
      }
    } catch (err) {
      setError('Payment confirmation failed');
      console.error('Payment confirmation error:', err);
    }
    setLoading(false);
  };

  const shownAmount = serverQuote ? serverQuote.amountCents : amount;
  const shownCount = serverQuote && Number.isFinite(serverQuote.voterCount) ? serverQuote.voterCount : totalCount;
  const shownTier = serverQuote ? serverQuote.tierLabel : tierLabel;

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem', color: '#112030' }}>
          {intent === 'send-to-doornoc' ? 'Purchase required to send to DoorNoc' : 'Complete Your Purchase'}
        </h3>
        {intent === 'send-to-doornoc' && (
          <div style={{
            padding: '10px 14px',
            backgroundColor: '#e8f4f8',
            border: '1px solid #b8dadd',
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 13,
            color: '#17688e'
          }}>
            Only purchased lists can be transferred to DoorNoc. After payment we'll send the list automatically.
          </div>
        )}
        {isCountLoading ? (
          <p style={{ color: '#666', marginBottom: '1rem' }}>Calculating price...</p>
        ) : (
          <>
            <p style={{ color: '#666', marginBottom: '0.25rem' }}>
              Amount: <strong>${(shownAmount / 100).toFixed(2)}</strong> for {shownCount.toLocaleString()} records
            </p>
            {shownTier && (
              <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>{shownTier}</p>
            )}
          </>
        )}
        <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '1rem', marginBottom: '1.5rem' }}>
          You'll receive a mailing-optimized CSV matching your search criteria.
        </p>
      </div>

      <div style={{
        position: 'relative',
        padding: '12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        marginBottom: '1rem',
        backgroundColor: isCountLoading ? '#f5f5f5' : '#fff'
      }}>
        <CardElement options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': { color: '#aab7c4' }
            }
          }
        }} />
      </div>

      {chargedIntentId && (
        <div style={{
          color: '#1e4620',
          marginBottom: '1rem',
          padding: '0.5rem',
          backgroundColor: '#edf7ed',
          border: '1px solid #b7dfb9',
          borderRadius: '4px',
          fontSize: '0.9rem'
        }}>
          Your card was charged. If the download does not start, use Retry confirmation below — you will not be charged again.
        </div>
      )}

      {(error || quoteError) && (
        <div style={{
          color: 'red',
          marginBottom: '1rem',
          padding: '0.5rem',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px',
          fontSize: '0.9rem'
        }}>
          {error || quoteError}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#f5f5f5',
            color: '#333',
            border: '1px solid #ccc',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading || isCountLoading || (!clientSecret && !chargedIntentId)}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: (loading || isCountLoading || (!clientSecret && !chargedIntentId)) ? '#ccc' : '#112030',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: (loading || isCountLoading || (!clientSecret && !chargedIntentId)) ? 'not-allowed' : 'pointer',
            fontSize: '1rem'
          }}
        >
          {loading ? 'Processing...' :
            isCountLoading ? 'Calculating price...' :
            chargedIntentId ? 'Retry confirmation' :
            `Pay $${(shownAmount / 100).toFixed(2)}`}
        </button>
      </div>
    </form>
  );
};

const CheckoutPopup = ({
  isOpen,
  onClose,
  amount = 0,
  productType,
  tierLabel,
  quoteError,
  searchParameters,
  onSuccess,
  isCountLoading,
  totalCount,
  intent
}) => {
  if (!isOpen) return null;

  const handleSuccess = (purchase) => {
    onSuccess(purchase);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        maxWidth: '550px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        <Elements stripe={stripePromise}>
          <StripePaymentForm
            amount={amount}
            productType={productType}
            tierLabel={tierLabel}
            quoteError={quoteError}
            onSuccess={handleSuccess}
            onCancel={onClose}
            searchParameters={searchParameters}
            isCountLoading={isCountLoading}
            totalCount={totalCount}
            intent={intent}
          />
        </Elements>
      </div>
    </div>
  );
};

export default CheckoutPopup;
